import { Fragment } from 'preact/jsx-runtime';
import { useRef } from 'preact/hooks';
import ProductGrid from '@nespresso/catalog-components-react/containers/ProductGrid';
import { type Product } from '@/domain/Product';
import CardBase from '../CardBase/CardBase.tsx';
import PromotionCard from '../PromotionCard/PromotionCard.tsx';
import type { PromotionCardDefinition } from '../../config/index.ts';
import styles from './ProductCardsGrid.module.scss';
import ProductTypeCard from './ProductTypeCard.tsx';
import useMarketConfig from '@/widgets/hooks/useMarketConfig.tsx';
import mapRowProducts from '../../utils/mapRowProducts.ts';
import {
  FAVORITE_LIST,
  FILTERED_LIST_ITEM_NAME,
  RAISED_BY,
  trackProductGroupImpression
} from '../../Tracking.ts';
import { getAllCards } from '../../utils/cards.ts';
import useRoute from '@/widgets/hooks/useRoute.tsx';

export type ProductCardsGridProps = {
  products: Product[];
  promotionCards?: PromotionCardDefinition[];
  title?: string;
  displayingCollection?: boolean;
};

// Lazyload images out of the fold - this number is based on the fact that
// usually on most displays only the first 4-6 images are visible on the
// page and so we can afford to lazyload most of the PLP product images
const LAZYLOAD_PRODUCT_CARD_IMAGE_THRESHOLD = 4;

export default function ProductCardsGrid({
  products = [],
  promotionCards = [],
  title,
  displayingCollection = false
}: ProductCardsGridProps) {
  const { inFavorites } = useRoute();
  const gridRef = useRef<HTMLDivElement>(null);
  const {
    financialSettings: { defaultCurrency },
    discoveryOfferBundleProducts
  } = useMarketConfig();

  const trackingList = !displayingCollection
    ? FILTERED_LIST_ITEM_NAME
    : title || RAISED_BY;

  const cards = getAllCards({
    products,
    displayingCollection,
    promotionCards
  });

  const handleProductImpression = (
    product: Product,
    trackingPosition: number
  ) => {
    if (!gridRef?.current) return;

    const gridColumns = Number(
      window
        .getComputedStyle(gridRef.current)
        .getPropertyValue('--grid-columns')
    );

    const rowProducts = mapRowProducts(product, cards, gridColumns);

    if (rowProducts.length) {
      trackProductGroupImpression(
        rowProducts,
        defaultCurrency,
        trackingPosition,
        trackingList,
        rowProducts.some((p) => discoveryOfferBundleProducts?.includes(p.id)),
        inFavorites ? FAVORITE_LIST : RAISED_BY
      );
    }
  };

  // const handlePromotionCardClick = (
  //   event: MouseEvent,
  //   url?: string,
  //   target?: PromotionCardCtaLinkTarget
  // ) => {
  //   // @todo - handle CTA click here + ITNP-123970
  // };

  return (
    <ProductGrid className={styles.grid} ref={gridRef}>
      {cards.map((card) => (
        <Fragment key={card.cardId}>
          {card.type === 'promotion' && (
            <PromotionCard
              promotion={card.promo}
              class={styles.promo}
              onClick={() => {}}
            />
          )}
          {card.type === 'product' && (
            <ProductTypeCard
              product={card.product}
              lazyLoadProductImage={
                card.index >= LAZYLOAD_PRODUCT_CARD_IMAGE_THRESHOLD
              }
              trackingList={trackingList}
              trackingPosition={card.index + 1}
              onPurchaseSectionImpression={() =>
                handleProductImpression(card.product, card.index + 1)
              }
              itemListName={trackingList}
            />
          )}
        </Fragment>
      ))}
    </ProductGrid>
  );
}

ProductCardsGrid.Skeleton = function Skeleton() {
  return (
    <ProductGrid className={styles.grid}>
      {Array(8)
        .fill(0)
        .map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <CardBase.Skeleton key={index} />
        ))}
    </ProductGrid>
  );
};
